import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { AlertTriangle, ArrowLeft, ChevronDown, ChevronRight, Download, ExternalLink, FileSignature, Info, Plus, Save, ShoppingCart, Trash2, Wand2 } from 'lucide-react'
import {
  Button,
  Card,
  ConfirmDialog,
  DataTable,
  EmptyState,
  ErrorState,
  IconButton,
  Input,
  LoadingState,
  PageHeader,
  RadioGroup,
  Select,
  StatusBadge,
  Switch,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea,
  textColumn,
  useToast,
  type ColumnDef,
} from '@/components/ui'
import { apiClient } from '@/lib/api.ts'
import { useQuery } from '@tanstack/react-query'
import {
  useAddContractLink,
  useContract,
  useContractGroups,
  useContracts,
  useContractVersions,
  useCreateContractVersion,
  useDeleteContract,
  useDeleteContractVersion,
  useDerivedContracts,
  useOrderContractAtEset,
  useProducts,
  useRemoveContractLink,
  useTariffs,
  useUpdateContract,
} from './api/contractsApi.ts'
import { useSlaProfiles } from './api/slaApi.ts'
import { useContractPlausibility, type PlausibilityFinding } from './api/plausibilityApi.ts'
import { useContractCustomFields } from './api/customFieldsApi.ts'
import type {
  ContractCustomField,
  ContractDirection,
  ContractDocument,
  ContractLink,
  ContractStatus,
  CustomFieldSection,
  UpdateContractInput,
} from './types.ts'
import { SapPartnerPicker } from './components/SapPartnerPicker.tsx'
import { PositionenTab } from './components/PositionenTab.tsx'
import {
  documentDownloadUrl,
  useContractDocuments,
  useContractTemplates,
  useDeleteContractDocument,
  useGenerateContractDocument,
  useUpdateContractDocument,
} from './api/templatesApi.ts'
import { UploadSignedModal } from './components/UploadSignedModal.tsx'
import { ZeitbudgetSection } from './sections/ZeitbudgetSection.tsx'
import {
  Dropdown,
  DropdownContent,
  DropdownItem,
  DropdownSeparator,
  DropdownTrigger,
} from '@/components/ui'
import { CheckCircle2, MoreHorizontal, Send, Upload } from 'lucide-react'

interface AuditLogEntry {
  id: string
  contractId: string
  tableName: string
  recordId: string
  fieldName: string
  oldValue: string | null
  newValue: string | null
  userId: string | null
  userName: string | null
  source: 'MANUAL' | 'IMPORT_ALSO' | 'IMPORT_ESET' | 'BILLING_RUN' | 'PRICE_ADJUSTMENT' | 'SYSTEM'
  reason: string | null
  createdAt: string
}

const STATUS_OPTIONS: { value: ContractStatus; label: string }[] = [
  { value: 'draft', label: 'Entwurf' },
  { value: 'active', label: 'Aktiv' },
  { value: 'paused', label: 'Pausiert' },
  { value: 'terminated', label: 'Gekündigt' },
  { value: 'expired', label: 'Abgelaufen' },
]

function isoDate(d: string | null | undefined): string {
  if (!d) return ''
  return new Date(d).toISOString().slice(0, 10)
}

export function ContractDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { toast } = useToast()
  const { data: contract, isLoading, error, refetch } = useContract(id)
  const update = useUpdateContract(id ?? '')
  const del = useDeleteContract()
  const orderEset = useOrderContractAtEset(id ?? '')
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [confirmEsetOrder, setConfirmEsetOrder] = useState(false)

  if (isLoading || !id) return <LoadingState label="Lade Vertrag …" />
  if (error) {
    return (
      <div className="p-4">
        <Button variant="ghost" size="sm" leadingIcon={<ArrowLeft className="w-4 h-4" />} onClick={() => navigate('/vertraege')}>
          Zurück zur Liste
        </Button>
        <ErrorState
          description="Vertrag konnte nicht geladen werden."
          details={error instanceof Error ? error.message : String(error)}
          onRetry={() => void refetch()}
        />
      </div>
    )
  }
  if (!contract) {
    return (
      <div className="p-4">
        <EmptyState title="Vertrag nicht gefunden" />
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title={contract.contractNumber}
        description={contract.name ?? '—'}
        breadcrumbs={[
          { label: 'Hauptmenü', to: '/dashboard' },
          { label: 'Verträge', to: '/vertraege' },
          { label: contract.contractNumber },
        ]}
        actions={
          <div className="flex items-center gap-2">
            <StatusBadge status={`contract_${contract.status}`} />
            {contract.esetPublicLicenseKey ? (
              <span className="inline-flex items-center gap-1 text-small text-green-700">
                ESET-Lizenz: {contract.esetPublicLicenseKey}
              </span>
            ) : (
              <Button
                variant="accent"
                size="sm"
                leadingIcon={<ShoppingCart className="w-4 h-4" />}
                onClick={() => setConfirmEsetOrder(true)}
                disabled={!contract.customerId}
                title={
                  !contract.customerId
                    ? 'Vertrag braucht einen Kunden, um eine Lizenz zu buchen'
                    : undefined
                }
              >
                Bei ESET buchen
              </Button>
            )}
            {contract.status === 'draft' ? (
              <Button
                variant="danger"
                size="sm"
                leadingIcon={<Trash2 className="w-4 h-4" />}
                onClick={() => setConfirmDelete(true)}
              >
                Löschen
              </Button>
            ) : null}
          </div>
        }
      />

      {contract.specialTerminationUntil && (
        <Card className="border-amber-200 bg-amber-50">
          <div className="flex items-start gap-3 px-4 py-3 text-small text-amber-800">
            <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <div className="space-y-1">
              <div className="font-medium">Sonderkündigungsrecht aktiv</div>
              <div>
                Aufgrund einer Hersteller-Preisanpassung &gt; 20 % steht dem Kunden bis zum{' '}
                <strong>{new Date(contract.specialTerminationUntil).toLocaleDateString('de-DE')}</strong>{' '}
                ein Sonderkündigungsrecht zu. Bitte prüfe Kundenkommunikation und Vertragsstatus.
              </div>
            </div>
          </div>
        </Card>
      )}

      <PlausibilityBanner contractId={id} />

      <Tabs defaultValue="stammdaten">
        <TabsList>
          <TabsTrigger value="stammdaten">Stammdaten</TabsTrigger>
          <TabsTrigger value="positionen">Positionen</TabsTrigger>
          {contract.direction === 'inbound' && (
            <TabsTrigger value="bezogene">Bezogene Verträge</TabsTrigger>
          )}
          {contract.timeBudgetMinutesPerUser != null && (
            <TabsTrigger value="zeitbudget">Zeitbudget</TabsTrigger>
          )}
          <TabsTrigger value="dateien">Dateien</TabsTrigger>
          <TabsTrigger value="versionen">Versionen</TabsTrigger>
          <TabsTrigger value="audit">Audit-Trail</TabsTrigger>
          {contract.timeBudgetMinutesPerUser != null && (
            <TabsTrigger value="zeitbudget">Zeitbudget</TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="stammdaten">
          <StammdatenTab
            contract={contract}
            onSave={(payload) =>
              update.mutate(payload, {
                onSuccess: () => toast({ title: 'Vertrag gespeichert', tone: 'success' }),
                onError: (err) =>
                  toast({
                    title: 'Speichern fehlgeschlagen',
                    description: err instanceof Error ? err.message : 'Unbekannter Fehler',
                    tone: 'danger',
                  }),
              })
            }
            saving={update.isPending}
          />
        </TabsContent>

        <TabsContent value="positionen">
          <PositionenTab contract={contract} />
        </TabsContent>

        {contract.direction === 'inbound' && (
          <TabsContent value="bezogene">
            <BezogeneVertraegeTab contractId={id} />
          </TabsContent>
        )}

        {contract.timeBudgetMinutesPerUser != null && (
          <TabsContent value="zeitbudget">
            <ZeitbudgetSection
              contractId={id}
              budgetMinutesPerUser={contract.timeBudgetMinutesPerUser}
              overtageHourlyRateCents={contract.timeBudgetOvertageHourlyRateCents}
            />
          </TabsContent>
        )}

        <TabsContent value="dateien">
          <DateienTab contractId={id} links={contract.links} />
        </TabsContent>

        <TabsContent value="versionen">
          <VersionenTab contractId={id} />
        </TabsContent>

        <TabsContent value="audit">
          <AuditTab contractId={id} />
        </TabsContent>
      </Tabs>

      <ConfirmDialog
        open={confirmDelete}
        title="Vertrag löschen?"
        message={`Vertrag „${contract.contractNumber}" wird unwiderruflich gelöscht. Möglich nur im Status „Entwurf".`}
        variant="danger"
        confirmLabel="Löschen"
        loading={del.isPending}
        onConfirm={() => {
          del.mutate(id, {
            onSuccess: () => {
              toast({ title: 'Vertrag gelöscht', tone: 'success' })
              navigate('/vertraege')
            },
            onError: (err) => {
              toast({
                title: 'Löschen nicht möglich',
                description: err instanceof Error ? err.message : 'Unbekannter Fehler',
                tone: 'danger',
              })
              setConfirmDelete(false)
            },
          })
        }}
        onCancel={() => setConfirmDelete(false)}
      />

      <ConfirmDialog
        open={confirmEsetOrder}
        title="Vertrag bei ESET buchen?"
        message={
          'Alle Vertragspositionen mit gemapptem ESET-Produktcode werden als ' +
          'einzelne Lizenzen bei ESET MSP gebucht. Mehrfach-Buchungen sind danach gesperrt.'
        }
        variant="default"
        confirmLabel="Jetzt buchen"
        loading={orderEset.isPending}
        onConfirm={() => {
          orderEset.mutate(undefined, {
            onSuccess: (result) => {
              setConfirmEsetOrder(false)
              const summary =
                `${result.orderedItems.length} Lizenz(en) gebucht` +
                (result.skippedItems.length > 0
                  ? ` · ${result.skippedItems.length} Position(en) übersprungen (kein eset_product_code)`
                  : '')
              toast({ title: 'ESET-Buchung erfolgreich', description: summary, tone: 'success' })
              void refetch()
            },
            onError: (err) => {
              setConfirmEsetOrder(false)
              const data = (err as { response?: { data?: { error?: string } } }).response?.data
              const message = data?.error ?? (err instanceof Error ? err.message : 'Unbekannter Fehler')
              toast({ title: 'ESET-Buchung fehlgeschlagen', description: message, tone: 'danger' })
            },
          })
        }}
        onCancel={() => setConfirmEsetOrder(false)}
      />
    </div>
  )
}

// ─── Plausibilitaets-Banner (Etappe 12) ──────────────────────────────────────

const PLAUSI_TYPE_LABELS: Record<PlausibilityFinding['type'], string> = {
  user_count_vs_license: 'Lizenz vs. Nutzer',
  asset_count_vs_position: 'Geräte-Anzahl',
  tier_jump_savings: 'Staffel-Sprung',
  contract_expiring: 'Ablaufender Vertrag',
  missing_party: 'Vertragspartei fehlt',
  zero_quantity: 'Soll-Menge 0',
}

function PlausibilityBanner({ contractId }: { contractId: string }) {
  const { data } = useContractPlausibility(contractId)
  const findings = data?.findings ?? []
  if (findings.length === 0) return null

  const critical = findings.filter((f) => f.severity === 'critical')
  const warning = findings.filter((f) => f.severity === 'warning')
  const info = findings.filter((f) => f.severity === 'info')

  const variant =
    critical.length > 0
      ? { border: 'border-red-200', bg: 'bg-red-50', text: 'text-red-800' }
      : warning.length > 0
        ? { border: 'border-amber-200', bg: 'bg-amber-50', text: 'text-amber-800' }
        : { border: 'border-blue-200', bg: 'bg-blue-50', text: 'text-blue-800' }

  return (
    <Card className={`${variant.border} ${variant.bg}`}>
      <div className={`flex items-start gap-3 px-4 py-3 text-small ${variant.text}`}>
        <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
        <div className="space-y-1">
          <div className="font-medium">
            Plausibilität: {critical.length} kritisch · {warning.length} Warnung · {info.length} Info
          </div>
          <ul className="list-disc list-inside space-y-0.5">
            {findings.slice(0, 5).map((f, i) => (
              <li key={i}>
                <span className="font-medium">{PLAUSI_TYPE_LABELS[f.type]}:</span> {f.message}
              </li>
            ))}
            {findings.length > 5 && <li>… und {findings.length - 5} weitere Hinweise.</li>}
          </ul>
        </div>
      </div>
    </Card>
  )
}

// ─── Stammdaten ─────────────────────────────────────────────────────────────

interface StammdatenTabProps {
  contract: NonNullable<ReturnType<typeof useContract>['data']>
  onSave: (payload: UpdateContractInput) => void
  saving: boolean
}

interface SectionCardProps {
  title: string
  description?: string
  children: ReactNode
}

function SectionCard({ title, description, children }: SectionCardProps) {
  return (
    <Card>
      <Card.Header>
        <div className="flex flex-col">
          <Card.Title>{title}</Card.Title>
          {description ? (
            <span className="text-caption text-gray-500">{description}</span>
          ) : null}
        </div>
      </Card.Header>
      <Card.Body className="space-y-4">{children}</Card.Body>
    </Card>
  )
}

interface CollapsibleSectionCardProps extends SectionCardProps {
  defaultOpen?: boolean
  badge?: ReactNode
}

function CollapsibleSectionCard({
  title,
  description,
  children,
  defaultOpen = false,
  badge,
}: CollapsibleSectionCardProps) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <Card>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full px-4 py-3 border-b border-gray-200 flex items-center justify-between gap-3 text-left hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40"
        aria-expanded={open}
      >
        <div className="flex items-center gap-2">
          {open ? (
            <ChevronDown className="h-4 w-4 text-gray-500" />
          ) : (
            <ChevronRight className="h-4 w-4 text-gray-500" />
          )}
          <div className="flex flex-col">
            <span className="text-h4 text-gray-900">{title}</span>
            {description ? <span className="text-caption text-gray-500">{description}</span> : null}
          </div>
        </div>
        {badge}
      </button>
      {open ? <div className="p-4 space-y-4">{children}</div> : null}
    </Card>
  )
}

function StammdatenTab({ contract, onSave, saving }: StammdatenTabProps) {
  const [contractNumber, setContractNumber] = useState(contract.contractNumber)
  const [direction, setDirection] = useState<ContractDirection>(contract.direction)
  const [customerId, setCustomerId] = useState(contract.customerId ?? '')
  const [partySapCardCode, setPartySapCardCode] = useState(contract.partySapCardCode ?? '')
  const [partyName, setPartyName] = useState(contract.partyName ?? '')
  const [productId, setProductId] = useState(contract.productId ?? '')
  const [tariffId, setTariffId] = useState(contract.tariffId ?? '')
  const [name, setName] = useState(contract.name ?? '')
  const [status, setStatus] = useState<ContractStatus>(contract.status)
  const [startDate, setStartDate] = useState(isoDate(contract.startDate))
  const [endDate, setEndDate] = useState(isoDate(contract.endDate))
  const [signingDate, setSigningDate] = useState(isoDate(contract.signingDate))
  const [billingStartDate, setBillingStartDate] = useState(isoDate(contract.billingStartDate))
  const [noticePeriodDays, setNoticePeriodDays] = useState(contract.noticePeriodDays)
  const [noticePeriodUnit, setNoticePeriodUnit] = useState<'days' | 'months'>(contract.noticePeriodUnit ?? 'days')
  const [autoRenew, setAutoRenew] = useState(contract.autoRenew)
  const [notes, setNotes] = useState(contract.notes ?? '')
  const [parentSupplierContractId, setParentSupplierContractId] = useState(contract.parentSupplierContractId ?? '')
  const [slaProfileId, setSlaProfileId] = useState(contract.slaProfileId ?? '')
  const [legacyContractNumber, setLegacyContractNumber] = useState(contract.legacyContractNumber ?? '')
  const [description2, setDescription2] = useState(contract.description2 ?? '')
  const [minimumTermMonths, setMinimumTermMonths] = useState(
    contract.minimumTermMonths != null ? String(contract.minimumTermMonths) : '',
  )
  const [renewalTermMonths, setRenewalTermMonths] = useState(
    contract.renewalTermMonths != null ? String(contract.renewalTermMonths) : '',
  )
  const [terminationReceivedDate, setTerminationReceivedDate] = useState(isoDate(contract.terminationReceivedDate))
  const [terminationRegisteredDate, setTerminationRegisteredDate] = useState(isoDate(contract.terminationRegisteredDate))
  const [terminationEffectiveDate, setTerminationEffectiveDate] = useState(isoDate(contract.terminationEffectiveDate))
  const [terminationDocumentId, setTerminationDocumentId] = useState(contract.terminationDocumentId ?? '')
  const [billingPeriodMode, setBillingPeriodMode] = useState<'calendar' | 'contract'>(contract.billingPeriodMode ?? 'calendar')
  const [billingTiming, setBillingTiming] = useState<'advance' | 'arrears'>(contract.billingTiming ?? 'advance')
  const [contractGroupId, setContractGroupId] = useState(contract.contractGroupId ?? '')
  const [timeBudgetMinutesPerUser, setTimeBudgetMinutesPerUser] = useState<number | null>(
    contract.timeBudgetMinutesPerUser ?? null,
  )
  const [timeBudgetOvertageHourlyRateCents, setTimeBudgetOvertageHourlyRateCents] = useState<number | null>(
    contract.timeBudgetOvertageHourlyRateCents ?? null,
  )
  const [customValues, setCustomValues] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {}
    for (const [k, v] of Object.entries(contract.customFieldValues ?? {})) {
      initial[k] = v ?? ''
    }
    return initial
  })

  const { data: products = [] } = useProducts({ active: true })
  const { data: tariffs = [] } = useTariffs(productId ? { productId, active: true } : {})
  const { data: inboundContracts = [] } = useContracts({ direction: 'inbound' })
  const { data: slaProfiles = [] } = useSlaProfiles()
  const { data: contractGroups = [] } = useContractGroups()
  const { data: customFields = [] } = useContractCustomFields()

  useEffect(() => {
    setContractNumber(contract.contractNumber)
    setDirection(contract.direction)
    setCustomerId(contract.customerId ?? '')
    setPartySapCardCode(contract.partySapCardCode ?? '')
    setPartyName(contract.partyName ?? '')
    setProductId(contract.productId ?? '')
    setTariffId(contract.tariffId ?? '')
    setName(contract.name ?? '')
    setStatus(contract.status)
    setStartDate(isoDate(contract.startDate))
    setEndDate(isoDate(contract.endDate))
    setSigningDate(isoDate(contract.signingDate))
    setBillingStartDate(isoDate(contract.billingStartDate))
    setNoticePeriodDays(contract.noticePeriodDays)
    setNoticePeriodUnit(contract.noticePeriodUnit ?? 'days')
    setAutoRenew(contract.autoRenew)
    setNotes(contract.notes ?? '')
    setParentSupplierContractId(contract.parentSupplierContractId ?? '')
    setSlaProfileId(contract.slaProfileId ?? '')
    setLegacyContractNumber(contract.legacyContractNumber ?? '')
    setDescription2(contract.description2 ?? '')
    setMinimumTermMonths(contract.minimumTermMonths != null ? String(contract.minimumTermMonths) : '')
    setRenewalTermMonths(contract.renewalTermMonths != null ? String(contract.renewalTermMonths) : '')
    setTerminationReceivedDate(isoDate(contract.terminationReceivedDate))
    setTerminationRegisteredDate(isoDate(contract.terminationRegisteredDate))
    setTerminationEffectiveDate(isoDate(contract.terminationEffectiveDate))
    setTerminationDocumentId(contract.terminationDocumentId ?? '')
    setBillingPeriodMode(contract.billingPeriodMode ?? 'calendar')
    setBillingTiming(contract.billingTiming ?? 'advance')
    setContractGroupId(contract.contractGroupId ?? '')
    setTimeBudgetMinutesPerUser(contract.timeBudgetMinutesPerUser ?? null)
    setTimeBudgetOvertageHourlyRateCents(contract.timeBudgetOvertageHourlyRateCents ?? null)
    const next: Record<string, string> = {}
    for (const [k, v] of Object.entries(contract.customFieldValues ?? {})) {
      next[k] = v ?? ''
    }
    setCustomValues(next)
  }, [contract])

  const productOptions = useMemo(
    () => [{ value: '', label: '— Optional —' }, ...products.map((p) => ({ value: p.id, label: p.name }))],
    [products],
  )

  const tariffOptions = useMemo(
    () => [
      { value: '', label: productId ? '— Tarif wählen —' : '— Erst Produkt wählen —' },
      ...tariffs.map((t) => ({ value: t.id, label: t.name })),
    ],
    [tariffs, productId],
  )

  const supplierContractOptions = useMemo(
    () => [
      { value: '', label: '— Keine Quelle —' },
      ...inboundContracts.map((c) => ({
        value: c.id,
        label: `${c.contractNumber} · ${c.name ?? c.partyName ?? 'Lieferantenvertrag'}`,
      })),
    ],
    [inboundContracts],
  )

  const contractGroupOptions = useMemo(
    () => [
      { value: '', label: '— Keine Gruppe —' },
      ...contractGroups
        .filter((g) => g.active || g.id === contractGroupId)
        .map((g) => ({
          value: g.id,
          label: g.billingLookaheadDays > 0 ? `${g.name} · Lookahead ${g.billingLookaheadDays} Tage` : g.name,
        })),
    ],
    [contractGroups, contractGroupId],
  )

  const slaProfileOptions = useMemo(
    () => [
      { value: '', label: '— Kein SLA-Profil —' },
      ...slaProfiles
        .filter((p) => p.active || p.id === slaProfileId)
        .map((p) => ({
          value: p.id,
          label: `${p.name} · ${(p.guaranteedAvailabilityBp / 100).toFixed(2).replace('.', ',')} %`,
        })),
    ],
    [slaProfiles, slaProfileId],
  )

  const fieldsBySection = useMemo(() => {
    const map = new Map<CustomFieldSection, ContractCustomField[]>()
    for (const f of customFields) {
      if (!f.active) continue
      const arr = map.get(f.section) ?? []
      arr.push(f)
      map.set(f.section, arr)
    }
    for (const arr of map.values()) arr.sort((a, b) => a.sortOrder - b.sortOrder || a.label.localeCompare(b.label))
    return map
  }, [customFields])

  const hasTerminationData = Boolean(
    terminationReceivedDate || terminationRegisteredDate || terminationEffectiveDate || terminationDocumentId,
  )

  function setCustomValue(fieldId: string, value: string) {
    setCustomValues((prev) => ({ ...prev, [fieldId]: value }))
  }

  function renderCustomField(field: ContractCustomField) {
    const value = customValues[field.id] ?? ''
    const labelWithRequired = field.required ? `${field.label} *` : field.label
    switch (field.fieldType) {
      case 'textarea':
        return (
          <Textarea
            key={field.id}
            label={labelWithRequired}
            hint={field.hint ?? undefined}
            rows={3}
            value={value}
            onChange={(e) => setCustomValue(field.id, e.currentTarget.value)}
          />
        )
      case 'number':
        return (
          <Input
            key={field.id}
            label={labelWithRequired}
            hint={field.hint ?? undefined}
            type="number"
            value={value}
            onChange={(e) => setCustomValue(field.id, e.currentTarget.value)}
          />
        )
      case 'date':
        return (
          <Input
            key={field.id}
            label={labelWithRequired}
            hint={field.hint ?? undefined}
            type="date"
            value={value}
            onChange={(e) => setCustomValue(field.id, e.currentTarget.value)}
          />
        )
      case 'select': {
        const options = [
          { value: '', label: '— Bitte wählen —' },
          ...(field.optionsJson ?? []).map((o) => ({ value: o.value, label: o.label })),
        ]
        return (
          <Select
            key={field.id}
            label={labelWithRequired}
            hint={field.hint ?? undefined}
            value={value}
            onValueChange={(v) => setCustomValue(field.id, v)}
            options={options}
          />
        )
      }
      case 'boolean':
        return (
          <Switch
            key={field.id}
            label={labelWithRequired}
            hint={field.hint ?? undefined}
            checked={value === 'true'}
            onChange={(e) => setCustomValue(field.id, e.currentTarget.checked ? 'true' : 'false')}
          />
        )
      case 'text':
      default:
        return (
          <Input
            key={field.id}
            label={labelWithRequired}
            hint={field.hint ?? undefined}
            value={value}
            onChange={(e) => setCustomValue(field.id, e.currentTarget.value)}
          />
        )
    }
  }

  function renderSectionExtras(section: CustomFieldSection) {
    const list = fieldsBySection.get(section)
    if (!list || list.length === 0) return null
    return <>{list.map(renderCustomField)}</>
  }

  function buildCustomValuesPayload(): Record<string, string | null> {
    const payload: Record<string, string | null> = {}
    const knownIds = new Set(customFields.map((f) => f.id))
    for (const id of knownIds) {
      const v = customValues[id]
      payload[id] = v && v.trim() !== '' ? v : null
    }
    return payload
  }

  function handleSave() {
    onSave({
      contractNumber: contractNumber.trim() || undefined,
      direction,
      customerId: customerId || null,
      partySapCardCode: partySapCardCode || null,
      partyName: partyName || null,
      productId: productId || null,
      tariffId: tariffId || null,
      name: name || null,
      status,
      startDate,
      endDate: endDate || null,
      signingDate: signingDate || null,
      billingStartDate: billingStartDate || null,
      noticePeriodDays,
      noticePeriodUnit,
      autoRenew,
      notes: notes || null,
      parentSupplierContractId: direction === 'outbound' ? (parentSupplierContractId || null) : null,
      slaProfileId: slaProfileId || null,
      legacyContractNumber: legacyContractNumber || null,
      description2: description2 || null,
      minimumTermMonths: minimumTermMonths ? Number(minimumTermMonths) : null,
      renewalTermMonths: renewalTermMonths ? Number(renewalTermMonths) : null,
      terminationReceivedDate: terminationReceivedDate || null,
      terminationRegisteredDate: terminationRegisteredDate || null,
      terminationEffectiveDate: terminationEffectiveDate || null,
      terminationDocumentId: terminationDocumentId || null,
      billingPeriodMode,
      billingTiming,
      contractGroupId: contractGroupId || null,
      timeBudgetMinutesPerUser: timeBudgetMinutesPerUser,
      timeBudgetOvertageHourlyRateCents: timeBudgetOvertageHourlyRateCents,
      customFieldValues: customFields.length > 0 ? buildCustomValuesPayload() : undefined,
    })
  }

  const additionalFields = fieldsBySection.get('additional') ?? []

  return (
    <div className="space-y-4 pb-20">
      <SectionCard title="Identifikation & Status" description="Vertragsnummer, Historien-Referenz und aktueller Status.">
        <div className="grid grid-cols-3 gap-3">
          <Input
            label="Vertragsnummer"
            hint="Fortlaufend; manuell überschreibbar."
            value={contractNumber}
            onChange={(e) => setContractNumber(e.currentTarget.value)}
          />
          <Input
            label="Alte SAP-Vertragsnummer"
            hint="VNR aus dem alten System für die Historie."
            value={legacyContractNumber}
            onChange={(e) => setLegacyContractNumber(e.currentTarget.value)}
          />
          <Select<ContractStatus>
            label="Status"
            value={status}
            onValueChange={setStatus}
            options={STATUS_OPTIONS}
          />
        </div>

        <RadioGroup<ContractDirection>
          label="Richtung"
          value={direction}
          onValueChange={setDirection}
          orientation="horizontal"
          options={[
            { value: 'outbound', label: 'Ausgehend (Kunde)' },
            { value: 'inbound', label: 'Eingehend (Lieferant)' },
          ]}
        />

        {renderSectionExtras('identification')}
      </SectionCard>

      <SectionCard title="Vertragspartei" description="Geschäftspartner aus SAP und Anzeigename auf dem Dokument.">
        <SapPartnerPicker
          label={direction === 'outbound' ? 'Kunde (SAP)' : 'Lieferant (SAP)'}
          hint={
            direction === 'outbound'
              ? 'Live-Suche über den SAP Service Layer (alle SAP-Kunden).'
              : 'Live-Suche über den SAP Service Layer (alle SAP-Lieferanten).'
          }
          value={partySapCardCode}
          initialName={partyName}
          onChange={(cardCode, partner) => {
            setPartySapCardCode(cardCode)
            if (partner) setPartyName(partner)
            if (!cardCode) setCustomerId('')
          }}
        />

        <Input
          label="Anzeigename Vertragspartei"
          hint="Wird im Vertrag angezeigt; bei SAP-Auswahl automatisch übernommen."
          value={partyName}
          onChange={(e) => setPartyName(e.currentTarget.value)}
        />

        {renderSectionExtras('party')}
      </SectionCard>

      <SectionCard title="Leistung" description="Produkt, Tarif, optionale Bezugsquelle und Service-Level.">
        <Select
          label="Produkt / Vertragstyp"
          hint={
            products.length === 0
              ? 'Noch keine Produkte angelegt — siehe Tab „Produktkatalog" auf der Verträge-Seite.'
              : undefined
          }
          value={productId}
          onValueChange={(v) => {
            setProductId(v)
            setTariffId('')
          }}
          options={productOptions}
        />

        <Select
          label="Tarif"
          hint={productId ? 'Aktive Tarife für das gewählte Produkt' : 'Erst Produkt wählen, dann Tarif aus den aktiven Versionen wählen'}
          value={tariffId}
          onValueChange={setTariffId}
          options={tariffOptions}
          disabled={!productId}
        />

        {direction === 'outbound' && (
          <Select
            label="Quelle (Lieferantenvertrag)"
            hint="Optional: Inbound-Vertrag, aus dem dieser Kundenvertrag bezogen wird (z. B. ALSO-MSP-Pool)."
            value={parentSupplierContractId}
            onValueChange={setParentSupplierContractId}
            options={supplierContractOptions}
          />
        )}

        <Input
          label="Vertragsbezeichnung"
          value={name}
          onChange={(e) => setName(e.currentTarget.value)}
        />

        <Input
          label="Beschreibung 2"
          hint={'Zweite Vertragsbezeichnung (entspricht SAP „Beschreibung 2").'}
          value={description2}
          onChange={(e) => setDescription2(e.currentTarget.value)}
        />

        <Select
          label="SLA-Profil"
          hint="Service-Level für Verfügbarkeitsgarantien und Gutschriften (Etappe 11)."
          value={slaProfileId}
          onValueChange={setSlaProfileId}
          options={slaProfileOptions}
        />

        {renderSectionExtras('service')}
      </SectionCard>

      <SectionCard title="Laufzeit & Fristen" description="Datumsfelder, Mindestlaufzeit und Kündigungsfrist.">
        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Abschlussdatum"
            hint="Datum der Vertragsunterzeichnung (SAP: Abschlussdatum)."
            type="date"
            value={signingDate}
            onChange={(e) => setSigningDate(e.currentTarget.value)}
          />
          <Input
            label="Abrechnungsbeginn"
            hint="Datum, ab dem abgerechnet wird (SAP: Abrechnungsbeginn). Leer = wie Vertragsbeginn."
            type="date"
            value={billingStartDate}
            onChange={(e) => setBillingStartDate(e.currentTarget.value)}
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Vertragsbeginn"
            hint="Formaler Vertragsstart — z. B. Originaldatum bei Übernahmen."
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.currentTarget.value)}
            required
          />
          <Input
            label="Ende"
            hint="Leer = unbefristet"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.currentTarget.value)}
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Mindestlaufzeit (Monate)"
            hint="Wie lange muss der Vertrag mindestens laufen, bevor gekündigt werden kann."
            type="number"
            value={minimumTermMonths}
            onChange={(e) => setMinimumTermMonths(e.currentTarget.value)}
          />
          <Input
            label="Verlängerung (Monate)"
            hint="Verlängerungs-Intervall bei automatischer Verlängerung."
            type="number"
            value={renewalTermMonths}
            onChange={(e) => setRenewalTermMonths(e.currentTarget.value)}
          />
        </div>

        <div className="grid grid-cols-[1fr_140px] gap-3">
          <Input
            label={`Kündigungsfrist (${noticePeriodUnit === 'months' ? 'Monate' : 'Tage'})`}
            type="number"
            value={String(noticePeriodDays)}
            onChange={(e) => setNoticePeriodDays(Number(e.currentTarget.value) || 0)}
          />
          <Select<'days' | 'months'>
            label="Einheit"
            value={noticePeriodUnit}
            onValueChange={setNoticePeriodUnit}
            options={[
              { value: 'days', label: 'Tage' },
              { value: 'months', label: 'Monate' },
            ]}
          />
        </div>

        <Switch
          label="Automatische Verlängerung"
          checked={autoRenew}
          onChange={(e) => setAutoRenew(e.currentTarget.checked)}
        />

        {renderSectionExtras('term')}
      </SectionCard>

      <SectionCard title="Abrechnung" description="Periode, Timing, Sammelrechnung und SAP-Konditionen.">
        <div className="grid grid-cols-2 gap-3">
          <Select<'calendar' | 'contract'>
            label="Abrechnung nach Zeitraum"
            hint="Kalenderbezogen = 1. → Ultimo. Vertragsbezogen = ab Vertragsbeginn + Periode (z. B. Microsoft 365 P1Y)."
            value={billingPeriodMode}
            onValueChange={setBillingPeriodMode}
            options={[
              { value: 'calendar', label: 'Kalenderbezogen' },
              { value: 'contract', label: 'Vertragsbezogen' },
            ]}
          />
          <Select<'advance' | 'arrears'>
            label="Abrechnung im"
            value={billingTiming}
            onValueChange={setBillingTiming}
            options={[
              { value: 'advance', label: 'Voraus' },
              { value: 'arrears', label: 'Nachgang' },
            ]}
          />
        </div>

        <Select
          label="Vertragsgruppe (Sammelrechnung)"
          hint="Verträge derselben Gruppe landen auf einer Rechnung. Lookahead steuert das Vorausziehen zukünftiger Perioden."
          value={contractGroupId}
          onValueChange={setContractGroupId}
          options={contractGroupOptions}
        />

        <div className="rounded-md border border-blue-100 bg-blue-50 px-3 py-2 flex gap-2 items-start text-small text-blue-800">
          <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <div className="space-y-2 w-full">
            <div className="font-medium">Aus SAP übernommen</div>
            <div className="grid grid-cols-2 gap-3">
              <Input
                label="Zahlungsbedingung (SAP)"
                hint="GroupNum aus SAP. Leer = Standard aus Geschäftspartner."
                value={contract.sapPaymentTermsCode ?? ''}
                disabled
              />
              <Input
                label="Zahlweg (SAP)"
                hint="Code aus SAP. Wird automatisch aus dem Geschäftspartner gezogen."
                value={contract.sapPaymentMethodCode ?? ''}
                disabled
              />
            </div>
          </div>
        </div>

        {renderSectionExtras('billing')}
      </SectionCard>

      <CollapsibleSectionCard
        title="Kündigung"
        description="Eingangsdatum, Eintragung und Wirksamkeit der Kündigung."
        defaultOpen={hasTerminationData}
        badge={
          hasTerminationData ? (
            <StatusBadge status="contract_terminated" label="Daten erfasst" />
          ) : (
            <span className="text-caption text-gray-400">leer</span>
          )
        }
      >
        <div className="grid grid-cols-3 gap-3">
          <Input
            label="Eingegangen am"
            type="date"
            value={terminationReceivedDate}
            onChange={(e) => setTerminationReceivedDate(e.currentTarget.value)}
          />
          <Input
            label="Eingetragen am"
            type="date"
            value={terminationRegisteredDate}
            onChange={(e) => setTerminationRegisteredDate(e.currentTarget.value)}
          />
          <Input
            label="Wirksam ab"
            type="date"
            value={terminationEffectiveDate}
            onChange={(e) => setTerminationEffectiveDate(e.currentTarget.value)}
          />
        </div>

        <Input
          label="Kündigungs-Dokument-ID"
          hint="Optional: ID eines Dokuments aus dem Dateien-Tab als Beleg der Kündigung."
          value={terminationDocumentId}
          onChange={(e) => setTerminationDocumentId(e.currentTarget.value)}
        />

        {renderSectionExtras('termination')}
      </CollapsibleSectionCard>

      {additionalFields.length > 0 && (
        <SectionCard
          title="Zusatzfelder"
          description={'Mandantenspezifische Felder (Konfiguration unter „Verträge → Zusatzfelder“).'}
        >
          {additionalFields.map(renderCustomField)}
        </SectionCard>
      )}

      <CollapsibleSectionCard
        title="Zeitbudget"
        description="Stundenbudget pro Techniker und Überschreitungssatz."
        defaultOpen={timeBudgetMinutesPerUser != null}
        badge={
          timeBudgetMinutesPerUser != null ? (
            <StatusBadge status="contract_active" label="Aktiv" />
          ) : (
            <span className="text-caption text-gray-400">nicht konfiguriert</span>
          )
        }
      >
        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Budget pro Techniker (Minuten/Monat)"
            type="number"
            hint="Leer lassen um Zeitbudget zu deaktivieren."
            value={timeBudgetMinutesPerUser != null ? String(timeBudgetMinutesPerUser) : ''}
            onChange={(e) =>
              setTimeBudgetMinutesPerUser(e.currentTarget.value ? Number(e.currentTarget.value) : null)
            }
          />
          <Input
            label="Überschreitungssatz (Cent/Stunde)"
            type="number"
            hint="Preis pro Überstunden-Stunde in Cent (z.B. 9000 = 90,00 €)."
            value={timeBudgetOvertageHourlyRateCents != null ? String(timeBudgetOvertageHourlyRateCents) : ''}
            onChange={(e) =>
              setTimeBudgetOvertageHourlyRateCents(
                e.currentTarget.value ? Number(e.currentTarget.value) : null,
              )
            }
          />
        </div>
      </CollapsibleSectionCard>

      <SectionCard title="Notizen">
        <Textarea
          label="Interne Notizen"
          rows={4}
          value={notes}
          onChange={(e) => setNotes(e.currentTarget.value)}
        />
      </SectionCard>

      <div className="sticky bottom-0 z-sticky -mx-4 px-4 py-3 bg-white/95 backdrop-blur border-t border-gray-200 flex justify-end shadow-card">
        <Button
          variant="primary"
          size="md"
          leadingIcon={<Save className="w-4 h-4" />}
          onClick={handleSave}
          loading={saving}
        >
          Stammdaten speichern
        </Button>
      </div>
    </div>
  )
}

// ─── Bezogene Vertraege (nur Inbound-Vertraege / MSP-Pool) ────────────────────

function BezogeneVertraegeTab({ contractId }: { contractId: string }) {
  const navigate = useNavigate()
  const { data, isLoading, error } = useDerivedContracts(contractId)
  const items = data?.items ?? []
  const summary = data?.summary ?? { count: 0, activeCount: 0 }

  const columns = useMemo<ColumnDef<typeof items[number], unknown>[]>(
    () => [
      textColumn('contractNumber', 'Vertrag', { size: 140 }),
      textColumn('customerName', 'Kunde'),
      textColumn('productName', 'Produkt'),
      {
        accessorKey: 'status',
        header: 'Status',
        size: 120,
        cell: ({ getValue }) => <StatusBadge status={`contract_${getValue() as string}`} />,
      },
      {
        accessorKey: 'startDate',
        header: 'Beginn',
        size: 120,
        cell: ({ getValue }) =>
          new Date(getValue() as string).toLocaleDateString('de-DE'),
      },
    ],
    [],
  )

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <Card>
          <Card.Body>
            <p className="text-sm text-gray-500">Bezogene Verträge gesamt</p>
            <p className="text-2xl font-semibold mt-1">{summary.count}</p>
          </Card.Body>
        </Card>
        <Card>
          <Card.Body>
            <p className="text-sm text-gray-500">Davon aktiv</p>
            <p className="text-2xl font-semibold mt-1">{summary.activeCount}</p>
          </Card.Body>
        </Card>
      </div>

      <DataTable
        data={items}
        columns={columns}
        loading={isLoading}
        error={error ? 'Bezogene Verträge konnten nicht geladen werden.' : null}
        getRowId={(c) => c.id}
        onRowClick={(c) => navigate(`/vertraege/${c.id}`)}
        emptyState={
          <EmptyState
            title="Noch keine bezogenen Verträge"
            description="Outbound-Verträge können diesen Inbound-Vertrag als Quelle wählen — sie erscheinen dann hier."
          />
        }
      />
    </div>
  )
}


// ─── Dateien / Links ────────────────────────────────────────────────────────

function DateienTab({ contractId, links }: { contractId: string; links: ContractLink[] }) {
  const { toast } = useToast()
  const add = useAddContractLink()
  const remove = useRemoveContractLink()
  const [label, setLabel] = useState('')
  const [url, setUrl] = useState('')

  const { data: templates = [] } = useContractTemplates({ active: true })
  const { data: documents = [] } = useContractDocuments(contractId)
  const generate = useGenerateContractDocument(contractId)
  const deleteDoc = useDeleteContractDocument(contractId)
  const updateDoc = useUpdateContractDocument(contractId)
  const [selectedTemplate, setSelectedTemplate] = useState('')
  const [uploadSignedFor, setUploadSignedFor] = useState<ContractDocument | null>(null)

  function setDocStatus(doc: ContractDocument, next: ContractDocument['status']) {
    updateDoc.mutate(
      { docId: doc.id, input: { status: next } },
      {
        onSuccess: () => toast({ title: 'Status aktualisiert', tone: 'success' }),
        onError: (err) =>
          toast({
            title: 'Statuswechsel fehlgeschlagen',
            description: err instanceof Error ? err.message : 'Unbekannter Fehler',
            tone: 'danger',
          }),
      },
    )
  }

  const templateOptions = useMemo(
    () => [
      { value: '', label: '— Vorlage wählen —' },
      ...templates.map((t) => ({ value: t.id, label: t.name })),
    ],
    [templates],
  )

  function handleGenerate() {
    if (!selectedTemplate) return
    generate.mutate(
      { templateId: selectedTemplate },
      {
        onSuccess: (doc) => {
          toast({ title: 'Dokument generiert', description: doc.fileName, tone: 'success' })
          setSelectedTemplate('')
        },
        onError: (err) =>
          toast({
            title: 'Generierung fehlgeschlagen',
            description: err instanceof Error ? err.message : 'Unbekannter Fehler',
            tone: 'danger',
          }),
      },
    )
  }

  function handleAdd() {
    if (!url && !label) return
    add.mutate(
      { contractId, label, url, kind: 'external_url' },
      {
        onSuccess: () => {
          setLabel('')
          setUrl('')
          toast({ title: 'Link ergänzt', tone: 'success' })
        },
        onError: (err) =>
          toast({
            title: 'Speichern fehlgeschlagen',
            description: err instanceof Error ? err.message : 'Unbekannter Fehler',
            tone: 'danger',
          }),
      },
    )
  }

  return (
    <div className="space-y-3">
      <Card>
        <Card.Header>
          <Card.Title>Aus Vorlage generieren</Card.Title>
        </Card.Header>
        <Card.Body className="space-y-3">
          <p className="text-caption text-gray-500">
            Eine Vertragsvorlage (DOCX) wird mit den Vertragsdaten gerendert und als Dokument gespeichert.
            Versand zur Unterschrift folgt mit Etappe 7b (Skribble).
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
            <Select
              wrapperClassName="md:col-span-2"
              label="Vorlage"
              hint={templates.length === 0 ? 'Keine aktive Vorlage — im Tab „Vorlagen" anlegen.' : undefined}
              value={selectedTemplate}
              onValueChange={setSelectedTemplate}
              options={templateOptions}
              disabled={templates.length === 0}
            />
            <Button
              variant="primary"
              size="sm"
              leadingIcon={<Wand2 className="w-4 h-4" />}
              onClick={handleGenerate}
              loading={generate.isPending}
              disabled={!selectedTemplate}
            >
              Generieren
            </Button>
          </div>
        </Card.Body>
      </Card>

      {documents.length > 0 ? (
        <DataTable<ContractDocument>
          data={documents}
          columns={[
            {
              accessorKey: 'fileName',
              header: 'Dokument',
              cell: ({ row }) => (
                <div>
                  <p className="text-small font-medium text-gray-900 inline-flex items-center gap-1">
                    <FileSignature className="w-3.5 h-3.5 text-gray-400" />
                    {row.original.fileName}
                  </p>
                  {row.original.templateName ? (
                    <p className="text-caption text-gray-500">{row.original.templateName}</p>
                  ) : null}
                </div>
              ),
            },
            {
              accessorKey: 'status',
              header: 'Status',
              size: 160,
              cell: ({ getValue }) => <StatusBadge status={`document_${getValue() as string}`} />,
            },
            {
              accessorKey: 'fileSize',
              header: 'Größe',
              size: 90,
              cell: ({ getValue }) => (
                <span className="text-caption tabular-nums">{((getValue() as number) / 1024).toFixed(0)} KB</span>
              ),
            },
            {
              accessorKey: 'generatedAt',
              header: 'Erzeugt',
              size: 160,
              cell: ({ getValue }) =>
                new Date(getValue() as string).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' }),
            },
            {
              id: '__actions__',
              header: '',
              size: 180,
              enableSorting: false,
              cell: ({ row }) => {
                const doc = row.original
                const canSendForSignature = doc.status === 'draft' || doc.status === 'finalized'
                const canUploadSigned =
                  doc.status === 'sent_for_signature' || doc.status === 'finalized' || doc.status === 'draft'
                const canDelete = doc.status === 'draft' || doc.status === 'finalized'
                return (
                  <div className="flex justify-end gap-1">
                    <IconButton
                      icon={<Download className="w-4 h-4" />}
                      aria-label="Herunterladen"
                      variant="ghost"
                      size="sm"
                      onClick={() =>
                        window.open(documentDownloadUrl(doc.id), '_blank', 'noopener')
                      }
                    />
                    {canSendForSignature ? (
                      <IconButton
                        icon={<Send className="w-4 h-4" />}
                        aria-label="Als zur Unterschrift markieren"
                        variant="ghost"
                        size="sm"
                        onClick={() => setDocStatus(doc, 'sent_for_signature')}
                      />
                    ) : null}
                    {canUploadSigned ? (
                      <IconButton
                        icon={<Upload className="w-4 h-4" />}
                        aria-label="Signiertes Dokument hochladen"
                        variant="ghost"
                        size="sm"
                        onClick={() => setUploadSignedFor(doc)}
                      />
                    ) : null}
                    <Dropdown>
                      <DropdownTrigger asChild>
                        <IconButton
                          icon={<MoreHorizontal className="w-4 h-4" />}
                          aria-label="Weitere Aktionen"
                          variant="ghost"
                          size="sm"
                        />
                      </DropdownTrigger>
                      <DropdownContent align="end">
                        {doc.status === 'draft' ? (
                          <DropdownItem onClick={() => setDocStatus(doc, 'finalized')}>
                            <CheckCircle2 className="w-3.5 h-3.5 mr-2" />
                            Auf „Final" setzen
                          </DropdownItem>
                        ) : null}
                        {doc.status === 'sent_for_signature' ? (
                          <>
                            <DropdownItem onClick={() => setDocStatus(doc, 'declined')}>
                              Status: abgelehnt
                            </DropdownItem>
                            <DropdownItem onClick={() => setDocStatus(doc, 'expired')}>
                              Status: abgelaufen
                            </DropdownItem>
                            <DropdownItem onClick={() => setDocStatus(doc, 'finalized')}>
                              Zurück zu „Final"
                            </DropdownItem>
                          </>
                        ) : null}
                        {doc.status === 'declined' || doc.status === 'expired' ? (
                          <DropdownItem onClick={() => setDocStatus(doc, 'sent_for_signature')}>
                            Erneut zur Unterschrift
                          </DropdownItem>
                        ) : null}
                        {canDelete ? (
                          <>
                            <DropdownSeparator />
                            <DropdownItem
                              tone="danger"
                              onClick={() =>
                                deleteDoc.mutate(doc.id, {
                                  onSuccess: () => toast({ title: 'Dokument gelöscht', tone: 'success' }),
                                  onError: (err) =>
                                    toast({
                                      title: 'Löschen fehlgeschlagen',
                                      description: err instanceof Error ? err.message : 'Unbekannter Fehler',
                                      tone: 'danger',
                                    }),
                                })
                              }
                            >
                              <Trash2 className="w-3.5 h-3.5 mr-2" />
                              Löschen
                            </DropdownItem>
                          </>
                        ) : null}
                      </DropdownContent>
                    </Dropdown>
                  </div>
                )
              },
            },
          ]}
          getRowId={(d) => d.id}
          searchable={false}
          emptyState={null}
        />
      ) : null}

      <UploadSignedModal
        open={Boolean(uploadSignedFor)}
        onOpenChange={(o) => !o && setUploadSignedFor(null)}
        contractId={contractId}
        document={uploadSignedFor}
      />

      <Card>
        <Card.Header>
          <Card.Title>Neuen Link / Dokument-Verweis ergänzen</Card.Title>
        </Card.Header>
        <Card.Body className="space-y-3">
          <p className="text-caption text-gray-500">
            Datei-Upload kommt mit Etappe 7 (Vertragsgenerator). Bis dahin: Verlinkung auf bestehende
            Dokumente (z. B. OneDrive, SAP-Dokumenten-Nummer, TANSS-Ticket).
          </p>
          <div className="grid grid-cols-2 gap-3">
            <Input label="Bezeichnung" value={label} onChange={(e) => setLabel(e.currentTarget.value)} />
            <Input label="URL" type="url" value={url} onChange={(e) => setUrl(e.currentTarget.value)} />
          </div>
        </Card.Body>
        <Card.Footer className="flex justify-end">
          <Button variant="primary" size="sm" leadingIcon={<Plus className="w-4 h-4" />} onClick={handleAdd} loading={add.isPending}>
            Hinzufügen
          </Button>
        </Card.Footer>
      </Card>

      <DataTable<ContractLink>
        data={links}
        columns={[
          textColumn<ContractLink>('label', 'Bezeichnung'),
          {
            accessorKey: 'url',
            header: 'URL',
            cell: ({ row }) =>
              row.original.url ? (
                <a
                  href={row.original.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-brand-700 hover:underline"
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                  {row.original.url}
                </a>
              ) : (
                <span className="text-caption text-gray-400">—</span>
              ),
          },
          {
            id: '__actions__',
            header: '',
            size: 60,
            enableSorting: false,
            cell: ({ row }) => (
              <div className="flex justify-end">
                <IconButton
                  icon={<Trash2 className="w-4 h-4" />}
                  aria-label="Entfernen"
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    remove.mutate(
                      { contractId, linkId: row.original.id },
                      {
                        onSuccess: () => toast({ title: 'Link entfernt', tone: 'success' }),
                      },
                    )
                  }
                />
              </div>
            ),
          },
        ]}
        getRowId={(l) => l.id}
        searchable={false}
        emptyState={<EmptyState title="Noch keine Verlinkungen" />}
      />
    </div>
  )
}

// ─── Versionen (Etappe 13a) ─────────────────────────────────────────────────

function VersionenTab({ contractId }: { contractId: string }) {
  const { toast } = useToast()
  const { data: versions = [], isLoading, error } = useContractVersions(contractId)
  const create = useCreateContractVersion(contractId)
  const del = useDeleteContractVersion(contractId)
  const [validFrom, setValidFrom] = useState(() => new Date().toISOString().slice(0, 10))
  const [comment, setComment] = useState('')

  function handleCreate() {
    if (!validFrom) {
      toast({ title: 'Gültig-ab fehlt', tone: 'danger' })
      return
    }
    create.mutate(
      { validFrom, comment: comment || null },
      {
        onSuccess: () => {
          toast({ title: 'Version angelegt', tone: 'success' })
          setComment('')
        },
        onError: (err) => toast({ title: 'Fehler', description: err.message, tone: 'danger' }),
      },
    )
  }

  return (
    <div className="space-y-4">
      <Card>
        <Card.Header>
          <h3 className="text-h4 font-semibold text-gray-900">Neue Version</h3>
          <p className="text-small text-gray-500">
            Markiert einen Konditions-Wechsel mit Gültig-ab-Datum und Kommentar. Erzeugt zusätzlich
            einen Snapshot des aktuellen Vertragsstands.
          </p>
        </Card.Header>
        <Card.Body className="space-y-3">
          <div className="grid grid-cols-[180px_1fr_auto] gap-3 items-end">
            <Input
              label="Gültig ab"
              type="date"
              value={validFrom}
              onChange={(e) => setValidFrom(e.currentTarget.value)}
            />
            <Input
              label="Kommentar"
              value={comment}
              onChange={(e) => setComment(e.currentTarget.value)}
              placeholder="z. B. Neuer Tarif ab Februar 2026"
            />
            <Button variant="primary" size="sm" onClick={handleCreate} loading={create.isPending}>
              Anlegen
            </Button>
          </div>
        </Card.Body>
      </Card>

      <Card>
        <Card.Header>
          <h3 className="text-h4 font-semibold text-gray-900">Historie</h3>
        </Card.Header>
        <Card.Body className="p-0">
          {isLoading ? (
            <LoadingState label="Lade Versionen …" />
          ) : error ? (
            <ErrorState
              description="Versionen konnten nicht geladen werden."
              details={error instanceof Error ? error.message : String(error)}
            />
          ) : versions.length === 0 ? (
            <EmptyState
              title="Noch keine Versionen"
              description="Lege eine Version an, um Konditions-Wechsel mit Gültig-ab-Datum festzuhalten."
            />
          ) : (
            <ul className="divide-y divide-gray-100">
              {versions.map((v) => (
                <li key={v.id} className="px-4 py-3 flex items-start gap-3">
                  <div className="flex-shrink-0">
                    <p className="text-small font-medium text-gray-900">
                      {new Date(v.validFrom).toLocaleDateString('de-DE')}
                    </p>
                    <p className="text-caption text-gray-500">
                      Erstellt {new Date(v.createdAt).toLocaleString('de-DE')}
                    </p>
                  </div>
                  <div className="flex-1 text-small text-gray-700">{v.comment ?? '—'}</div>
                  <IconButton
                    icon={<Trash2 className="w-4 h-4" />}
                    aria-label="Version löschen"
                    variant="ghost"
                    size="xs"
                    onClick={() => del.mutate(v.id)}
                  />
                </li>
              ))}
            </ul>
          )}
        </Card.Body>
      </Card>
    </div>
  )
}

// ─── Audit ──────────────────────────────────────────────────────────────────

const SOURCE_LABELS: Record<AuditLogEntry['source'], string> = {
  MANUAL: 'Manuell',
  IMPORT_ALSO: 'ALSO Import',
  IMPORT_ESET: 'ESET Import',
  BILLING_RUN: 'Abrechnungslauf',
  PRICE_ADJUSTMENT: 'Preisanpassung',
  SYSTEM: 'System',
}

const TABLE_LABELS: Record<string, string> = {
  contracts: 'Vertrag',
  contract_items: 'Position',
  contract_item_users: 'Benutzer',
  contract_item_assets: 'Asset',
  billing_items: 'Abrechnung',
  price_adjustments: 'Preisanpassung',
}

function AuditTab({ contractId }: { contractId: string }) {
  const { data: entries = [], isLoading, error } = useQuery<AuditLogEntry[]>({
    queryKey: ['contract-audit', contractId],
    queryFn: () =>
      apiClient
        .get(`/contracts/${contractId}/audit`)
        .then((r) => r.data),
  })

  const columns = useMemo<ColumnDef<AuditLogEntry, unknown>[]>(
    () => [
      {
        accessorKey: 'createdAt',
        header: 'Zeitpunkt',
        size: 150,
        cell: ({ getValue }) =>
          new Date(getValue() as string).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' }),
      },
      {
        accessorKey: 'tableName',
        header: 'Bereich',
        size: 110,
        cell: ({ getValue }) => TABLE_LABELS[getValue() as string] ?? (getValue() as string),
      },
      {
        accessorKey: 'fieldName',
        header: 'Feld',
        size: 160,
        cell: ({ getValue }) => <code className="text-caption">{getValue() as string}</code>,
      },
      {
        accessorKey: 'oldValue',
        header: 'Vorher',
        size: 160,
        cell: ({ getValue }) => {
          const v = getValue() as string | null
          if (v == null) return <span className="text-caption text-gray-400">—</span>
          return <span className="text-caption text-red-700 line-through">{v}</span>
        },
      },
      {
        accessorKey: 'newValue',
        header: 'Nachher',
        size: 160,
        cell: ({ getValue }) => {
          const v = getValue() as string | null
          if (v == null) return <span className="text-caption text-gray-400">—</span>
          return <span className="text-caption text-green-700">{v}</span>
        },
      },
      {
        accessorKey: 'userName',
        header: 'Benutzer',
        size: 140,
        cell: ({ getValue }) => (getValue() as string | null) ?? <span className="text-caption text-gray-400">System</span>,
      },
      {
        accessorKey: 'source',
        header: 'Quelle',
        size: 130,
        cell: ({ getValue }) => SOURCE_LABELS[getValue() as AuditLogEntry['source']] ?? (getValue() as string),
      },
      {
        accessorKey: 'reason',
        header: 'Begründung',
        cell: ({ getValue }) => {
          const v = getValue() as string | null
          if (!v) return <span className="text-caption text-gray-400">—</span>
          return <span className="text-caption">{v}</span>
        },
      },
    ],
    [],
  )

  function exportXlsx() {
    import('xlsx').then((XLSX) => {
      const rows = entries.map((e) => ({
        Zeitpunkt: new Date(e.createdAt).toLocaleString('de-DE'),
        Bereich: TABLE_LABELS[e.tableName] ?? e.tableName,
        Datensatz: e.recordId,
        Feld: e.fieldName,
        Vorher: e.oldValue ?? '',
        Nachher: e.newValue ?? '',
        Benutzer: e.userName ?? 'System',
        Quelle: SOURCE_LABELS[e.source] ?? e.source,
        Begründung: e.reason ?? '',
      }))
      const ws = XLSX.utils.json_to_sheet(rows)
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, 'Audit-Trail')
      XLSX.writeFile(wb, `audit-trail-${contractId}.xlsx`)
    })
  }

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Button
          variant="secondary"
          size="sm"
          leadingIcon={<Download className="w-4 h-4" />}
          onClick={exportXlsx}
          disabled={entries.length === 0}
        >
          Als XLSX exportieren
        </Button>
      </div>
      <DataTable<AuditLogEntry>
        data={entries}
        columns={columns}
        getRowId={(e) => e.id}
        loading={isLoading}
        error={error ? 'Audit-Trail konnte nicht geladen werden.' : null}
        searchable
        emptyState={<EmptyState title="Noch keine Audit-Einträge" description="Änderungen an Vertrags- und Positionsdaten erscheinen hier automatisch." />}
      />
    </div>
  )
}
